import { useId, useState, type FocusEvent, type ReactNode } from "react";
import { useWatch } from "react-hook-form";
import { PASSWORD_REQUIREMENTS, passwordStrengthScore } from "@nh/shared";
import {
  PasswordInput,
  type PasswordInputProps,
} from "../core/PasswordInput/PasswordInput";
import { PasswordStrength } from "../core/PasswordStrength/PasswordStrength";
import { useFieldConnection, type ConnectedFieldProps } from "./useFieldConnection";

/**
 * Para qué sirve esta contraseña. No es cosmético: es lo que decide el
 * `autoComplete`, y acertarle a eso es LO ÚNICO que hace que un gestor de
 * contraseñas ofrezca la guardada al entrar y proponga una nueva al
 * registrarse. Con el valor equivocado el gestor rellena la contraseña
 * vieja en el formulario de crear cuenta.
 */
export type PasswordPurpose = "current" | "new";

const AUTOCOMPLETE: Record<PasswordPurpose, string> = {
  current: "current-password",
  new: "new-password",
};

export interface PasswordFieldProps
  extends Omit<PasswordInputProps, "name">,
    Omit<ConnectedFieldProps, "name"> {
  /** Por defecto `"password"`. Para confirmar: `name="confirmPassword"`. */
  name?: string;
  /** `"current"` al entrar, `"new"` al crear cuenta o cambiarla. */
  purpose: PasswordPurpose;
  /**
   * Acción a la derecha del label — el "¿Olvidaste tu contraseña?" del
   * login. Va aquí y no debajo del campo porque debajo está reservado para
   * el error, y un enlace compitiendo con el mensaje de error lo entierra.
   */
  labelAction?: ReactNode;
  /**
   * Pinta el medidor de fuerza debajo del campo. `false` por defecto, y
   * eso es deliberado: solo tiene sentido donde se ESTÁ ELIGIENDO una
   * contraseña nueva.
   *
   * En el login estorba y además delata —enseñarle a quien escribe una
   * contraseña vieja que es "débil" no le sirve de nada ahí— y en
   * "confirmar contraseña" es directamente ruido: ahí no se elige nada,
   * se copia lo de arriba, y un segundo medidor idéntico duplica cuatro
   * renglones de requisitos por nada.
   */
  showStrength?: boolean;
}

/**
 * Contraseña conectada al formulario, con el ojo de mostrar/ocultar de
 * `PasswordInput` y el `autoComplete` que le toca según `purpose`.
 *
 * El medidor vive en un componente aparte (`WithStrength`) y no tras un
 * `if` dentro de este: es lo que evita que el `useWatch` que sigue al
 * valor en vivo se suscriba también en el login y en el campo de
 * confirmar, donde no se pinta nada. Un `useWatch` no se puede llamar
 * condicionalmente, pero un componente sí se puede no renderizar.
 */
export function PasswordField({ showStrength = false, ...rest }: PasswordFieldProps) {
  return showStrength ? <WithStrength {...rest} /> : <BaseField {...rest} />;
}

type BaseFieldProps = Omit<PasswordFieldProps, "showStrength">;

function BaseField({
  name = "password",
  purpose,
  registerOptions,
  disabled,
  error,
  onFocus,
  onBlur,
  ...rest
}: BaseFieldProps) {
  const field = useFieldConnection(name, registerOptions);

  return (
    <PasswordInput
      label="Contraseña"
      placeholder="••••••••"
      autoComplete={AUTOCOMPLETE[purpose]}
      {...rest}
      disabled={disabled ?? field.disabled}
      error={error ?? field.error}
      {...field.registration}
      onFocus={onFocus}
      // `register` devuelve SU propio `onBlur` (es con lo que
      // react-hook-form marca el campo como tocado). Se encadena en vez de
      // sustituirse: quien pase `onBlur` no puede romper la validación.
      onBlur={(event: FocusEvent<HTMLInputElement>) => {
        void field.registration.onBlur(event);
        onBlur?.(event);
      }}
    />
  );
}

/**
 * El campo + el medidor, con el enlace de accesibilidad resuelto.
 *
 * El medidor **solo aparece cuando hay algo escrito o el campo tiene el
 * foco**: un bloque gritando "Escribe una contraseña" con cuatro tachas
 * rojas sobre un formulario recién abierto no informa de nada, solo
 * empuja el botón de enviar hacia abajo y da la impresión de que algo ya
 * está mal.
 *
 * Mientras está oculto, su `id` tampoco viaja en el `aria-describedby`:
 * apuntar a un elemento que no existe deja al lector de pantalla sin nada
 * que leer justo donde promete que hay algo.
 *
 * Y mientras el medidor se ve, la PISTA se calla. Las dos dicen lo mismo
 * —"al menos 10 caracteres, con una letra y un número"— pero el medidor
 * además dice si ya se cumple. Enseñar ambas es repetir el requisito dos
 * veces con dos renglones de distancia; la pista se queda para cuando el
 * medidor no está, que es cuando sí hace falta la guía.
 */
function WithStrength(props: BaseFieldProps) {
  const name = props.name ?? "password";
  const [focused, setFocused] = useState(false);
  const strengthId = `nh-strength-${useId()}`;

  const watched: unknown = useWatch({ name, defaultValue: "" });
  const value = typeof watched === "string" ? watched : "";

  const visible = value.length > 0 || focused;

  /*
   * Cuándo se despliega la lista de requisitos.
   *
   * Solo mientras se está eligiendo la contraseña Y todavía falta algo.
   * En cuanto se cumplen todos, se pliega sola: ya no tiene nada que
   * decir y cuatro renglones de palomitas verdes empujan el botón de
   * enviar fuera de la pantalla. La barra y el resumen se quedan, que es
   * lo que confirma que va bien.
   *
   * NO se usa un temporizador de "dejó de escribir". Un plegado a los X
   * segundos castiga justo a quien más ayuda necesita: alguien que se
   * detiene a pensar porque le falta un requisito vería desaparecer
   * precisamente la línea que le dice cuál. Al perder el foco también se
   * pliega — ahí ya se dejó de escribir de verdad.
   */
  const complete = passwordStrengthScore(value) >= PASSWORD_REQUIREMENTS.length;
  const showRequirements = focused && !complete;

  return (
    <div className="grid gap-1.5">
      <BaseField
        {...props}
        hint={visible ? undefined : props.hint}
        // Se PASA, no se sustituye: `Input` compone este id con los del
        // error y la pista en vez de dejar que uno pise al otro.
        aria-describedby={visible ? strengthId : undefined}
        onFocus={(event) => {
          setFocused(true);
          props.onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          props.onBlur?.(event);
        }}
      />
      {visible && (
        <PasswordStrength
          value={value}
          describedById={strengthId}
          showRequirements={showRequirements}
        />
      )}
    </div>
  );
}
